const PENDING_SRC =
  '<div[^>]*class="[^"]*lesson_image_pending[^"]*"[^>]*>[\\s\\S]*?<\\/div>';

/** Fresh instance each call — a shared /g regex carries lastIndex between calls. */
const pendingRe = () => new RegExp(PENDING_SRC, "gi");

export const hasPendingImages = (html) =>
  new RegExp(PENDING_SRC, "i").test(String(html || ""));

export const stripPendingImageBlocks = (html = "") =>
  String(html).replace(pendingRe(), "");

const getAttr = (tag, name) => {
  try {
    const m = tag.match(new RegExp(`${name}\\s*=\\s*"([^"]*)"`, "i"));
    return m ? m[1] : "";
  } catch {
    return "";
  }
};

const norm = (s) => String(s || "").trim().toLowerCase();

/** Splits a streamed answer into text chunks and pending-image markers, in order. */
export function splitPendingImages(text) {
  const parts = [];
  if (!text) return parts;

  const re = pendingRe();
  let cursor = 0;
  let match;

  while ((match = re.exec(text)) !== null) {
    if (match.index > cursor) {
      parts.push({ kind: "text", value: text.slice(cursor, match.index) });
    }
    parts.push({
      kind: "pending",
      id: getAttr(match[0], "data-lesson-image"),
      concept: getAttr(match[0], "data-concept"),
    });
    cursor = match.index + match[0].length;
  }
  if (cursor < text.length) {
    parts.push({ kind: "text", value: text.slice(cursor) });
  }
  return parts;
}

/**
 * The placeholder arrives split across chunks (`<div class="lesson_im…`).
 * Cut the unterminated <div so raw markup never flashes — and if the fragment
 * is already recognisable, show its skeleton immediately instead of waiting
 * for the closing tag.
 */
export function splitTrailingPartial(text) {
  const open = text.lastIndexOf("<div");
  if (open === -1 || text.indexOf("</div>", open) !== -1) {
    return { body: text, partial: null };
  }
  const frag = text.slice(open);
  return {
    body: text.slice(0, open),
    partial: /lesson_image_pending/i.test(frag)
      ? {
          kind: "pending",
          id: getAttr(frag, "data-lesson-image"),
          concept: getAttr(frag, "data-concept"),
        }
      : null,
  };
}

/** Pure-image history rows keyed by concept — the lesson merge path. */
function buildConceptMap(histories) {
  const map = new Map();
  for (const h of histories || []) {
    if (!h?.answer || !/<img/i.test(h.answer)) continue;
    // only rows that are nothing but <img> tags are concept-image rows
    if (h.answer.replace(/<img[^>]*>/gi, "").trim() !== "") continue;
    map.set(norm(h.query), h.answer.trim());
  }
  return map;
}

/**
 * Produces the final answer HTML for one turn.
 *  A) backend merged images into that turn's row  → use the row wholesale
 *  B) images came back as separate concept rows   → splice them into the
 *     streamed text so nothing shifts position
 * Unresolvable placeholders are dropped rather than left as visible markup.
 */
export function resolveAnswerFromHistories(streamedAnswer, histories, uuid) {
  const list = Array.isArray(histories) ? histories : [];
  const entry =
    uuid != null ? list.find((h) => Number(h?.uuid) === Number(uuid)) : null;

  if (entry?.answer && !hasPendingImages(entry.answer) && /<img/i.test(entry.answer)) {
    return entry.answer;
  }

  const byConcept = buildConceptMap(list);
  return String(streamedAnswer || "").replace(pendingRe(), (block) => {
    const hit = byConcept.get(norm(getAttr(block, "data-concept")));
    if (!hit) console.warn("unresolved lesson image:", getAttr(block, "data-lesson-image"));
    return hit || "";
  });
}